import * as React from 'react';

import {colors} from '../colors/colors';
import {getEditionMetadata} from '../edition/get-edition-metadata';
import {
    getEditionStatusInfo,
    getTemplateEditionStatusInfo,
} from '../edition/get-edition-status-info';
// @ts-expect-error - imported from .js/.jsx file
import {BroadcastIcon} from '../icon/svg/broadcast-icon';
import {HalfAndHalfIcon} from '../icon/svg/half-and-half-icon';
// @ts-expect-error - imported from .js/.jsx file
import {NewsletterIcon} from '../icon/svg/newsletter-icon';
// @ts-expect-error - imported from .js/.jsx file
import {TemplateIcon} from '../icon/svg/template-icon';
import {StackLegacy} from '../layout/stack/stack-legacy';
import type {StatusBadgeProps} from '../status-badge/status-badge';
import {ThumbnailButtonCard} from '../thumbnail/thumbnail-button-card/thumbnail-button-card';
import {Body} from '../typography/body';

import type {Edition} from './types';

import styles from './edition-thumbnail-list.module.css';

const THUMBNAIL_HEIGHT = 196;
const CARD_WIDTH = 300;

type Props = {
    edition: Edition;
    onSelect?: (editionId: string) => void;
    displayIcon?: boolean;
    selectedId?: string | null | undefined;
    unsetCardWidth?: boolean;
    shouldTruncateName?: boolean;
    isDisabled?: boolean;
    disabledStatusDetail?: string | React.ReactNode;
};

export function EditionThumbnailCard(props: Props) {
    const {edition} = props;

    let icon;
    if (props.displayIcon) {
        if (edition.campaignType === 'BROADCAST') {
            icon = <BroadcastIcon size={15} className={styles['type-icon']} />;
        } else if (edition.campaignType === 'NEWSLETTER') {
            icon = <NewsletterIcon size={15} className={styles['type-icon']} />;
        } else if (edition.campaignType === 'TEMPLATE') {
            icon = <TemplateIcon size={13} className={styles['type-icon']} />;
        }
    }
    const editionMetadata = getEditionMetadata(edition);

    let statusInfo: StatusBadgeProps | null | undefined;
    if (props.isDisabled && props.disabledStatusDetail) {
        statusInfo = {
            level: 'info--grey',
            text: 'Not ready to send',
            detail: <div className={styles['disabled-tooltip']}>{props.disabledStatusDetail}</div>,
        };
    } else if (edition.campaignType === 'TEMPLATE') {
        statusInfo = getTemplateEditionStatusInfo(edition.status);
    } else {
        statusInfo =
            edition.campaignType === 'DRIP_SEQUENCE' && edition.status === 'SENDING'
                ? {level: 'info', text: 'Drip message'}
                : getEditionStatusInfo(edition.status);
    }

    const hasTertiaryInfo = edition.owner || edition.isABTesting;

    return (
        <ThumbnailButtonCard
            key={edition.id}
            title={edition.name}
            secondaryInfo={
                icon ? (
                    <div className='flex align-center'>
                        <div
                            aria-label={
                                edition.campaignType === 'BROADCAST' ? 'Broadcast' : 'Edition'
                            }
                            data-tip={
                                edition.campaignType === 'BROADCAST' ? `Broadcast` : `Edition`
                            }
                            data-for='icon-tip'
                        >
                            {icon}
                        </div>
                        {editionMetadata}
                    </div>
                ) : (
                    editionMetadata
                )
            }
            onClick={() => {
                if (props.onSelect) {
                    props.onSelect(edition.id);
                }
            }}
            tertiaryInfo={
                hasTertiaryInfo && (
                    <StackLegacy spacing={4}>
                        {edition.owner && (
                            <div style={{color: colors.grey}}>
                                <Body size='small'>{edition.owner.name}</Body>
                            </div>
                        )}
                        {edition.isABTesting && (
                            <div className='flex align-center' style={{color: colors.grey}}>
                                <HalfAndHalfIcon wrapWithDiv={false} size={13} />
                                <span style={{marginLeft: 4}}>A/B test</span>
                            </div>
                        )}
                    </StackLegacy>
                )
            }
            thumbnail={edition.thumbnailUrl}
            thumbnailHeight={THUMBNAIL_HEIGHT}
            cardWidth={props.unsetCardWidth ? undefined : CARD_WIDTH}
            isSelected={props.selectedId === edition.id}
            statusBadge={statusInfo}
            options={edition.options}
            shouldTruncateTitle={props.shouldTruncateName}
            isDisabled={props.isDisabled}
            showDisabledOverlay={Boolean(props.disabledStatusDetail)}
            backgroundColor={props.edition.backgroundColor}
        />
    );
}
